import { CodeSQLEditor, SQLEditorRef } from "../../components/codeEditor";
import styles from "./styles.module.scss";
import { ChangeEvent, MutableRefObject, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Feedback } from "../../components/feedback";
import { Alert, AlertDescription, Stack } from "@altimateai/lego";
import { Input } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import { useNavigate, useParams } from "react-router-dom";
import { InsightSection, QuerySqlLineage } from "./components";
import classNames from "classnames";
import { TypeWriterLoader } from "@/components/loader";
import {
  getQueryGroupSqlLineageById,
  getQueryGroupDetail,
  updateQueryGroupName,
} from "@/helpers/apis";
import * as Sentry from "@sentry/browser";
import { QueryListWithFilter } from "./Query";
import { GET_QUERY_GROUP_BY_ID, QueryGroupTabs } from "./constants";
import { ColorInsightTags } from "../../components/tags";
import dayjs from "dayjs";
import { GhostTab, Tab } from "../../components/tab/Tab";
import { formatNumber } from "@/uiCore";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useAppState } from "@/modules/app/useAppContext";
import { BackButton } from "@/components/button";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { ScrollArea } from "@altimateai/lego";
import { IconPencil } from "@tabler/icons-react";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { QueryGroup, UpdateQueryGroupNameRequest } from "@/pages/query/types";
import { GetQuerySQLLineageResponse } from "@/pages/explorer/types";
import { toSqlLineageProps } from "@/pages/query/components/QuerySqlLineage";
import { useFilteredOpportunities } from "@/modules/insights/useFilteredOpportunities";
import { QueriesTracking } from "@/lib/tracking/queries";

interface MetricItem {
  label: string;
  value: string | number;
}

interface GroupSelectedHeaderProps {
  queryHash: string;
  trimmedQuery: string;
  metrics: MetricItem[];
  tags: string[];
}

const GroupSelectedHeader = ({
  queryHash,
  trimmedQuery,
  metrics,
  tags,
}: GroupSelectedHeaderProps) => {
  // query_name unused
  const [isNameEditing, setIsNameEditing] = useState(false);
  const { currency } = useAppState();
  const queryClient = useQueryClient();
  const [groupName, setGroupName] = useState(trimmedQuery);

  const { mutate } = useMutation<
    Awaited<ReturnType<typeof updateQueryGroupName>>,
    Error,
    UpdateQueryGroupNameRequest
  >({
    mutationFn: (data: UpdateQueryGroupNameRequest) => updateQueryGroupName(queryHash, data),
    onSuccess: data => {
      if (data?.ok) {
        QueriesTracking.groupNameEdited(queryHash);
        setIsNameEditing(false);
        queryClient.invalidateQueries({ queryKey: [GET_QUERY_GROUP_BY_ID] });
      }
    },
  });
  return (
    <Stack align="start" gap="sm" className="al-bg-blue-50 p-3 border-radius-top">
      <BackButton defaultUrl="/query/groups" />
      <Stack direction="column" gap="sm" className="al-w-full">
        <Stack gap="xs" align="center">
          {!isNameEditing ? (
            <>
              <div className="text-primary">{groupName}</div>
              <IconPencil
                className={classNames("cursor-pointer", styles.edit_icon, {
                  [styles.active]: isNameEditing,
                })}
                size={16}
                onClick={e => {
                  e.stopPropagation();
                  setIsNameEditing(!isNameEditing);
                }}
                aria-label="Edit group name"
              />
            </>
          ) : (
            <>
              <Input
                disabled={!isNameEditing}
                className={styles.name_input}
                value={groupName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setGroupName(e.target.value)}
                placeholder={trimmedQuery}
              />
              <Button
                onClick={() => {
                  mutate({ group_name: groupName });
                }}
              >
                Save
              </Button>
              <Button variant="outline" onClick={() => setIsNameEditing(false)}>
                Cancel
              </Button>
            </>
          )}
          <div className="spacer" />
        </Stack>
        <Stack wrap className="flex-wrap">
          {metrics.map(item => (
            <div key={item.label} className="me-4">
              <span className="text-muted" aria-label={`${item.label} label`}>
                {item.label}:{" "}
              </span>
              {item.label === "Average cost" ||
              item.label === "Total cost" ||
              item.label === "Annualized cost" ? (
                <span aria-label={`${item.label} value`}>
                  {formatNumber(item.value as number, { currency })}
                </span>
              ) : (
                <span aria-label={`${item.label} value`}>{item.value}</span>
              )}
            </div>
          ))}
        </Stack>
        <ColorInsightTags tags={tags} />
      </Stack>
    </Stack>
  );
};

interface GroupTabsProps {
  selectedGroup: QueryGroup;
  sqlLineage: GetQuerySQLLineageResponse | undefined;
  queryText: string;
  parentEditorRef: MutableRefObject<SQLEditorRef | null>;
  showSideContent: boolean | string | null;
  setSelectedNode: (node: string) => void;
  selectedInsight: string | undefined;
  opportunityId: string | null;
  setOpportunityId: (id: string) => void;
  startDate: Date;
  endDate: Date;
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
}

const GroupTabs = ({
  selectedGroup,
  sqlLineage,
  queryText,
  parentEditorRef,
  showSideContent,
  setSelectedNode,
  selectedInsight,
  opportunityId,
  setOpportunityId,
  startDate,
  endDate,
  onDateRangeChange,
}: GroupTabsProps) => {
  const [selectedTab, setSelectedTab] = useState<string>(QueryGroupTabs.Opportunities);
  const [selectedOpportunityTab, setSelectedOpportunityTab] = useState(
    sqlLineage?.is_lineage ? "sql-visualizer" : "code"
  );

  const navigate = useNavigate();
  const tagsMeta = sqlLineage?.query_tags_meta;

  const filteredOpportunities = useFilteredOpportunities(sqlLineage?.tags ?? []);

  const tabs = [];
  if (sqlLineage && sqlLineage?.is_lineage) {
    tabs.push({
      label: "SQL Visualizer",
      value: "sql-visualizer",
      betaMode: true,
    });
  }
  tabs.push({ label: "Code", value: "code" });

  const onTabChange = (value: string) => {
    setSelectedOpportunityTab(value);
  };

  return (
    <div className={`bg-white pt-4 ${styles.groupTabs}`}>
      <Tab
        onSelectedTabChange={(v: string) => {
          setSelectedTab(v);
        }}
        tabs={[{ label: "Opportunities" }, { label: "Queries" }]}
      />

      {selectedTab === QueryGroupTabs.Opportunities ? (
        <div className="p-2 pt-0">
          <div className="d-inline-block mb-3">
            <GhostTab
              setSelectedTab={onTabChange}
              tabs={tabs}
              selectedTab={selectedOpportunityTab}
            />
          </div>
          <Stack direction="column" gap="sm">
            {selectedOpportunityTab === "sql-visualizer" ? (
              <QuerySqlLineage
                sqlLineage={sqlLineage ? toSqlLineageProps(sqlLineage) : null}
                selectedInsight={selectedInsight ?? null}
                showSideContent={Boolean(showSideContent)}
                onNodeSelect={(node: string) => {
                  setSelectedNode(node);
                  const _opportunityId = tagsMeta?.[node]?.[0]?.opportunity_id || "";
                  setOpportunityId(_opportunityId);
                }}
              />
            ) : null}

            {selectedOpportunityTab === "code" && (
              <CodeSQLEditor
                ref={parentEditorRef}
                value={queryText}
                height="36vh"
                lineNumbers={true}
                showCopy
                minimap
              />
            )}
            <div className={"al-bg-white al-p-3 al-my-2 al-rounded-md"}>
              <InsightSection
                opportunities={filteredOpportunities}
                sqlLineage={sqlLineage}
                queryText={queryText}
                totalRuns={String(selectedGroup.total_run)}
                selectedOpportunityId={opportunityId}
                setOpportunityId={(item: string) => {
                  setOpportunityId(item);
                  if (!sqlLineage) return;
                  parentEditorRef.current?.onParentResize();
                  const insightId = sqlLineage.tags.find(t => t.opportunity_id === item)?.id;
                  const _selectNode = (key: string) => {
                    setSelectedNode(key);
                    document.dispatchEvent(new CustomEvent("onSelectedNodes", { detail: key }));
                  };
                  if (!insightId) return _selectNode("");
                  for (const key in tagsMeta) {
                    for (const t of tagsMeta[key] || []) {
                      if (insightId === t.id) return _selectNode(key);
                    }
                  }
                  _selectNode("");
                }}
                startDate={startDate}
                endDate={endDate}
                onDateRangeChange={onDateRangeChange}
                showActionsDropdown={true}
                showTimeSavings
              />
            </div>
          </Stack>
        </div>
      ) : null}
      {selectedTab === QueryGroupTabs.Queries ? (
        <QueryListWithFilter
          setSelectedQuery={(v: string) =>
            navigate(`/query/${v}?from=/query/groups/${selectedGroup.query_hash}`)
          }
          queryHash={selectedGroup.query_hash}
        />
      ) : null}
    </div>
  );
};

interface MainContentProps {
  groupDetails: QueryGroup;
  queryHash: string | undefined;
  queryText: string;
  selectedInsight: string | undefined;
  sqlLineage: GetQuerySQLLineageResponse | undefined;
  setSelectedNode: (node: string) => void;
  parentEditorRef: MutableRefObject<SQLEditorRef | null>;
  showSideContent: boolean | string | null;
  startDate: Date;
  endDate: Date;
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
}

const MainContent = ({
  groupDetails,
  queryHash,
  queryText,
  selectedInsight,
  sqlLineage,
  setSelectedNode,
  parentEditorRef,
  showSideContent,
  startDate,
  endDate,
  onDateRangeChange,
}: MainContentProps) => {
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const opportunityId = searchParams.get("opportunityId");
  const setOpportunityId = (v: string) => updateSearchParamsByKey({ opportunityId: v });

  useEffect(() => {
    if (!opportunityId) return;
    const tagsMeta = sqlLineage?.query_tags_meta as
      | Record<string, Array<{ tag_rk?: string }>>
      | undefined;
    for (const key in tagsMeta || {}) {
      for (const t of tagsMeta?.[key] ?? []) {
        if (selectedInsight === t.tag_rk) {
          setSelectedNode(key);
          document.dispatchEvent(new CustomEvent("onSelectedNodes", { detail: key }));
          return;
        }
      }
    }
  }, []);

  return (
    <div className={classNames("al-flex al-flex-col al-h-[85dvh]", styles.query_details)}>
      <GroupSelectedHeader
        trimmedQuery={groupDetails.trimmed_query}
        tags={groupDetails.tags}
        metrics={[
          { label: "Total run", value: groupDetails.total_run },
          {
            label: "Average execution time",
            value: groupDetails.avg_exec_time,
          },
          {
            label: "Average cost",
            value: groupDetails.avg_cost,
          },
          {
            label: "Total cost",
            value: groupDetails.total_cost,
          },
          {
            label: "Annualized cost",
            value: groupDetails.annualized_cost,
          },
          {
            label: "Distinct warehouses",
            value: groupDetails.distinct_warehouses,
          },
          { label: "Distinct users", value: groupDetails.distinct_users },
        ]}
        queryHash={groupDetails.query_hash}
      />
      <ScrollArea className="al-flex-1">
        <GroupTabs
          selectedGroup={groupDetails}
          sqlLineage={sqlLineage}
          queryText={queryText}
          parentEditorRef={parentEditorRef}
          opportunityId={opportunityId}
          setOpportunityId={setOpportunityId}
          setSelectedNode={setSelectedNode}
          selectedInsight={selectedInsight}
          showSideContent={showSideContent}
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={onDateRangeChange}
        />

        <div className="divider al-w-full mb-2" />
        {queryHash ? (
          <Feedback
            feedback_id={queryHash}
            feedback_type="query_tag"
            resetWorkflow={() => {}}
            afterSubmit={() => {}}
            className=""
          />
        ) : null}
      </ScrollArea>
    </div>
  );
};

const GroupDetails = () => {
  const { queryHash } = useParams();
  useBreadcrumb(queryHash ?? "Details", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "Groups", pathname: "/query/groups" },
    ],
  });

  const editorRef = useRef<SQLEditorRef>(null);
  const [selectedNode, setSelectedNode] = useState("");
  const { searchParams } = useFiltersBySearchParams();

  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );

  const { data: sqlLineage, isLoading: isSqlLineageLoading } = useQuery({
    queryKey: ["GET_QUERY_GROUP_SQL_LINEAGE_BY_ID", queryHash, startDate, endDate],
    queryFn: () =>
      getQueryGroupSqlLineageById(queryHash!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() ?? "",
      }),
    enabled: Boolean(queryHash),
    meta: {
      onError: (err: unknown) => {
        if (err === "canceled") {
          Sentry.captureException(new Error("SQL Lineage timeout"));
        }
      },
    },
  });
  const {
    data: groupDetails,
    isLoading: isGrouploading,
    error,
  } = useQuery({
    queryKey: [GET_QUERY_GROUP_BY_ID, queryHash, startDate, endDate],
    queryFn: async (): Promise<QueryGroup | undefined> => {
      const result = await getQueryGroupDetail(queryHash!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() ?? "",
      });
      return result as QueryGroup | undefined;
    },
    enabled: Boolean(queryHash),
  });

  const opportunityId = searchParams.get("opportunityId");
  const selectedInsight = (sqlLineage?.tags ?? []).find(t => t.id === opportunityId)?.tag_rk;

  const showSideContent = selectedNode || opportunityId;
  const queryText = groupDetails?.full_query || "";

  if (isSqlLineageLoading || isGrouploading) {
    return (
      <div className="al-h-full align-content-center">
        <TypeWriterLoader
          label="Analyzing query might take a few seconds"
          loadingTexts={[
            "Looking at query metadata",
            "Analyzing query pattern",
            "Fetching query insights",
          ]}
        />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{String(error)}</AlertDescription>
      </Alert>
    );
  }

  if (!groupDetails) {
    return (
      <Alert variant="destructive">
        <AlertDescription>No data for query group found</AlertDescription>
      </Alert>
    );
  }

  return (
    <MainContent
      queryHash={queryHash}
      groupDetails={groupDetails}
      parentEditorRef={editorRef}
      queryText={queryText}
      selectedInsight={selectedInsight}
      setSelectedNode={setSelectedNode}
      sqlLineage={sqlLineage}
      showSideContent={showSideContent}
      startDate={startDate}
      endDate={endDate}
      onDateRangeChange={onUpdate}
    />
  );
};

export { GroupDetails };
